import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SearchRequest, SearchResult } from 'gung-list';
import { Assortment, AssortmentService, GungBase, Product, ProductConcept, ProductService } from 'gung-standard';
import { GungUser as User } from 'gung-standard';
import { filter, first, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { ColmecProductAssortmentParentService } from './colmec-product-assortment-parent.service';

@Injectable({
  providedIn: 'root'
})
export class ColmecProductService extends ProductService {
  private colmecProductAssortmentParentService = inject(ColmecProductAssortmentParentService);
  private assortmentService = inject(AssortmentService);

  public getProductsByAssortment(assortmentId: string): Observable<Product[]> {
    return super.getProductsByAssortment(assortmentId).pipe(
      first(),
      switchMap(products => this.mergeAssortmentsIntoProducts(products))
    );
  }

  public getProductsByIds(productIds: string[]): Observable<Product[]> {
    return super.getProductsByIds(productIds).pipe(
      first(),
      switchMap(products => this.mergeAssortmentsIntoProducts(products))
    );
  }

  public getFullProductsByIds(productIds: string[]): Observable<Product[]> {
    return super.getFullProductsByIds(productIds).pipe(
      first(),
      switchMap(products => this.mergeAssortmentsIntoProducts(products))
    );
  }

  public getProduct(productId: string, noCache?: boolean): Observable<Product> {
    return super.getProduct(productId, noCache).pipe(
      first(),
      switchMap(product => this.mergeAssortmentsIntoProducts([product])),
      map(products => products[0])
    );
  }

  public getPagedProducts(searchRequest: SearchRequest): Observable<SearchResult<Product>> {
    return super.getPagedProducts(searchRequest).pipe(
      first(),
      map(res => {
        return {
          ...res,
          items: res.items.map(product => this.productMap(product))
        };
      }),
      mergeMap(res => {
        const ids = res.items.map(p => p.id);
        return forkJoin([
          of(res),
          this.colmecProductAssortmentParentService
            .postProductAssortmentParentByProductIds(ids /* , searchRequest.assortment */)
            .pipe(
              filter(assortmentParent => !!assortmentParent),
              first()
            )
        ]);
      }),
      switchMap(([res, productAssortmentParent]) => {
        if (productAssortmentParent) {
          res.items.map(p => {
            if (productAssortmentParent[p.id]) {
              const paths = productAssortmentParent[p.id].map(path => path.id);
              paths.shift();
              p.extra.assortmentParentPath = paths.join('/');
            }
            return p;
          });
        }
        return of(res);
      })
    );
  }

  private mergeAssortmentsIntoProducts(protuctsToMap: Product[]): Observable<Product[]> {
    return of(protuctsToMap).pipe(
      first(),
      switchMap(products => {
        const ids = products.map(p => p.id);
        return forkJoin([
          of(products),
          this.colmecProductAssortmentParentService.postProductAssortmentParentByProductIds(ids).pipe(
            filter(assortmentParent => !!assortmentParent),
            first()
          )
        ]);
      }),
      switchMap(([products, productAssortmentParent]) => {
        const assortmentIds: string[] = [];

        if (productAssortmentParent) {
          products.map(p => {
            if (!p) {
              return;
            }
            if (productAssortmentParent[p.id]) {
              const paths = productAssortmentParent[p.id].map(path => path.id);
              paths.shift();
              p.extra.assortmentParentPath = paths.join('/');

              const assortmentPaths = paths.filter(path => path.startsWith('s')); // assortment
              if (assortmentPaths.length > 0) {
                const productAssortmentId = assortmentPaths[assortmentPaths.length - 1]; // get last
                p.extra.parentAssortmentId = productAssortmentId;
                assortmentIds.push(productAssortmentId);
              }
            }
            return p;
          });
        }
        return of({ products, uniqueAssortmentIds: Array.from(new Set(assortmentIds)) });
      }),
      switchMap(data =>
        forkJoin({
          products: of(data.products),
          assortments:
            data.uniqueAssortmentIds && data.uniqueAssortmentIds.length > 0
              ? forkJoin(
                  data.uniqueAssortmentIds.map(assortmentId =>
                    this.assortmentService.getAssortment(assortmentId).pipe(first())
                  )
                )
              : of([] as Assortment[])
        })
      ),
      map(({ products, assortments }) => {
        for (const product of products) {
          if (!product) {
            continue;
          }

          if (
            !product.extra.images ||
            product.extra.images.length === 0 ||
            product.extra.images[0].s3Uri === 'images/no-image.jpg'
          ) {
            const assortment = assortments.find(assort => assort.id === product.extra.parentAssortmentId);

            if (assortment && assortment.extra.images && assortment.extra.images.length > 0) {
              product.extra.images = assortment.extra.images;
            }
          }
        }

        return products;
      })
    );
  }

  getSearchAssortments(searchTerm: string, assortmentId?: string): Observable<SearchAssortments> {
    let requestAssortment;
    if (!!assortmentId) {
      requestAssortment = of({ assortment: assortmentId });
    } else {
      requestAssortment = this.authService.getCurrentUser().pipe(first());
    }
    return requestAssortment.pipe(
      switchMap((user: User) => {
        const url = `/json/feature/search-assortments/${user.assortment}?query=${searchTerm}`;
        let headers = new HttpHeaders();
        headers = headers.set('maxAge', '1800');
        return this.http.get<SearchAssortments>(url, { headers });
      })
    );
  }
}

export interface SearchAssortments {
  assortments: { assortment: Assortment; path: GungBase[] }[];
  concepts: { concept: ProductConcept; path: GungBase[] }[];
}
